import { USER_CREDIT_QUERY_KEY } from "@/components/Referral/hooks/useUserCreditQuery";
import { USER_PLAN_QUERY_KEY } from "@/components/Root/hooks/useUserPlanData";
import { useNavigate } from "@/lib/router";
import { TBillingInterval } from "@/types/app";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useIntercom } from "react-use-intercom";
import { CHECKOUT_TYPE } from "../../constants.checkout";
import { CHECKOUT_EVENTS } from "../../events.checkout";
import { handlePaymentExecute } from "../../handlers";
import { CheckoutService } from "../../service.checkout";
import { TCheckoutType } from "../../types.checkout";

/** Shared so any page can read the in-flight state with `useIsMutating`. */
export const CHECKOUT_MUTATION_KEY = "checkout-execute";

interface UseCheckoutMutationOptions {
  checkoutType: TCheckoutType;
  billingPeriod: TBillingInterval;
  successRedirectUrl?: string;
  failureRedirectUrl?: string;
  countryCode?: string;
}

type CheckoutMutationVars = Parameters<typeof handlePaymentExecute>[0] & {
  _tracking?: {
    cartItems?: Array<{ plan_name: string; quantity: number }>;
    total?: number;
  };
};

export function useCheckoutMutation({
  checkoutType,
  billingPeriod,
  successRedirectUrl,
  failureRedirectUrl,
  countryCode,
}: UseCheckoutMutationOptions) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { showNewMessage } = useIntercom();

  return useMutation({
    mutationKey: [CHECKOUT_MUTATION_KEY, checkoutType],
    mutationFn: ({ _tracking: _t, ...option }: CheckoutMutationVars) =>
      handlePaymentExecute({
        ...option,
        checkoutType,
        payload: {
          ...option.payload,
          ...(countryCode && { country_code: countryCode }),
          ...(successRedirectUrl !== undefined && {
            success_redirect_url: successRedirectUrl,
          }),
          ...(failureRedirectUrl !== undefined && {
            failure_redirect_url: failureRedirectUrl,
          }),
        },
      }),
    onSuccess: (result, variables) => {
      if (result.status) {
        qc.invalidateQueries({ queryKey: [USER_PLAN_QUERY_KEY] });
        qc.invalidateQueries({ queryKey: [USER_CREDIT_QUERY_KEY] });

        toast.success("Purchase completed successfully!");

        CheckoutService.track(CHECKOUT_EVENTS.CHECKOUT_COMPLETED, {
          checkout_context: checkoutType,
          transaction_id: result.data,
          cart_items: variables._tracking?.cartItems,
          total: variables._tracking?.total,
          plan_interval: billingPeriod,
          resolved_country: countryCode,
        });

        if (checkoutType === CHECKOUT_TYPE.MANAGE) {
          navigate("/");
        } else if (checkoutType === "plan_migration") {
          navigate("/?newMigration=true");
        } else {
          navigate("/payment-successful");
        }
      } else if (result.error?.code === 302) {
        window.location.href = result.error.data.redirect_url;
      }
    },
    onError: (error, variables) => {
      toast.error("Something went wrong during checkout. Please try again.");
      showNewMessage("Hi, I need help with my checkout.");

      const apiError = (error as any)?.response?.data?.error;
      CheckoutService.track(CHECKOUT_EVENTS.CHECKOUT_FAILED, {
        checkout_context: checkoutType,
        error_code:
          apiError?.code ?? (error instanceof Error ? error.name : "unknown"),
        error_message:
          apiError?.message ??
          (error instanceof Error ? error.message : "Unknown error"),
        total: variables._tracking?.total,
        resolved_country: countryCode,
      });

      CheckoutService.logger.error("Checkout error:", error);
    },
  });
}
